import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useDebounceFn } from 'ahooks';
import { InView } from 'react-intersection-observer';

import { useAccountCompanyId } from 'application/hooks';
import SuggestionUtils from 'application/utils/SuggestionUtils';
import CompanyTagChip, {
  VISIBLE_TITLE_LENGTH,
  VISIBLE_TITLE_LENGTH_MOBILE,
} from 'application/components/CompanyTagChip';
import {
  useNotificationContext,
  NOTIFICATION_DEFAULT_DELAY,
  NotificationColors,
} from 'application/providers/NotificationProvider';

import useCompanyTags from '@entities/company/api/useCompanyTags';
import { PaginationInfoType } from '@shared/api/graphql/generated';

import { DropdownItemDataType } from '@zvuk-b2b/react-uikit/ui/Dropdown';
import Spinner from '@zvuk-b2b/react-uikit/ui/Spinner';
import { Colors } from '@zvuk-b2b/react-uikit/ui/types';
import useMedia from '@zvuk-b2b/react-uikit/toolkit/useMedia';

import { CompanyTagType } from '@entities/company/api/types';
import { CompanyTagWithoutCompanyIdType } from './types';
import { TEXTS } from './texts';
import {
  OPTIONS,
  MAX_TAG_TITLE_LENGTH,
  AUTOCOMPLETE_MAX_VISIBLE_OPTIONS,
  AUTOCOMPLETE_TAGS_PAGE_SIZE,
  AUTOCOMPLETE_DROPDOWN_MENU_MARGIN,
} from './consts';

import styles from './index.module.scss';
import { CompanyTagsContext } from './CompanyTagsContext';

const CompanyTagsProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const companyId = useAccountCompanyId();
  const { isDesktop } = useMedia();
  const notification = useNotificationContext();
  const [requestTags, result] = useCompanyTags();

  const [value, setValue] = useState<string>('');
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [localTagTitles, setLocalTagTitles] = useState<string[]>([]);
  const [isChanged, setIsChanged] = useState<boolean>(false);
  const [filterSelectedTags, setFilterSelectedTags] = useState<DropdownItemDataType[]>([]);
  const [isSetLoading, setIsSetLoading] = useState<boolean>(false);

  const tagsMapRef = useRef<Map<string, string>>(new Map());
  const pageRef = useRef<number>(0);
  const paginationInfoRef = useRef<PaginationInfoType | undefined>(result.data.paginationInfo);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const modalContentRef = useRef<HTMLDivElement | null>(null);

  const initTags = (initialTags: CompanyTagWithoutCompanyIdType[]) => {
    const newTagIds: string[] = [];

    initialTags.forEach((tag) => {
      newTagIds.push(tag.id);
      tagsMapRef.current.set(tag.id, tag.title);
    });

    setTagIds(newTagIds);
    setLocalTagTitles([]);
  };

  const getSelectedTagsSnapshot = () => tagIds.map((id) => ({ id, title: tagsMapRef.current.get(id) || '' }));

  const request = async (searchQuery: string) => {
    pageRef.current = 0;

    await requestTags(
      {
        companyId,
        title: searchQuery,
      },
      { page: pageRef.current, pageSize: AUTOCOMPLETE_TAGS_PAGE_SIZE }
    );
  };

  const loadMore = async (searchQuery: string) => {
    const page = (paginationInfoRef.current?.page || 0) + 1;

    if (page <= pageRef.current) return;

    pageRef.current = page;

    await result.loadMore(
      {
        companyId,
        title: searchQuery,
      },
      { page, pageSize: AUTOCOMPLETE_TAGS_PAGE_SIZE }
    );
  };

  const { run: debouncedRequest, cancel: cancelDebouncedRequest } = useDebounceFn(
    (query: string) => request(query),
    OPTIONS
  );

  useEffect(() => {
    if (result.data.tags && result.data.tags.length !== 0) {
      result.data.tags.forEach((tag) => tagsMapRef.current.set(tag.id, tag.title));
    }

    if (result.data.paginationInfo) {
      paginationInfoRef.current = result.data.paginationInfo;
    }
  }, [result.data.tags]);

  useEffect(() => {
    const onResize = (event: Event) => {
      if (!isDesktop) {
        const visualViewport = event.target as VisualViewport;
        if (modalContentRef.current) {
          modalContentRef.current.style.setProperty(
            '--dropdown-margin-bottom',
            `${window.innerHeight - visualViewport.height}px`
          );
        }
      }
    };

    window.visualViewport?.addEventListener('resize', onResize);

    return () => window.visualViewport?.removeEventListener('resize', onResize);
  }, []);

  const resetTags = async () => {
    cancelDebouncedRequest();
    setValue('');

    pageRef.current = 0;
    await result.refetch({
      filters: { companyId, title: '' },
      pagination: { page: pageRef.current, pageSize: AUTOCOMPLETE_TAGS_PAGE_SIZE },
    });
  };

  const formatTagTitle = (raw: string): string => {
    const trimmed = raw.trim();
    if (!trimmed) return trimmed;
    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
  };

  const onAddTagClick = async () => {
    const title = formatTagTitle(value);

    if (!title) {
      notification.showNotification({
        type: NotificationColors.ERROR,
        children: TEXTS.MODAL_TAG_TITLE_CONTAIN_WHITE_SPACES_TEXT,
        delay: NOTIFICATION_DEFAULT_DELAY,
      });
      setValue('');
      return;
    }

    if (title.length > MAX_TAG_TITLE_LENGTH) {
      notification.showNotification({
        type: NotificationColors.ERROR,
        children: TEXTS.MODAL_AUTOCOMPLETE_ADD_TOO_LONG_VALUE_FOR_TAG,
        delay: NOTIFICATION_DEFAULT_DELAY,
      });
      return;
    }

    const isTitleAlreadySelected = tagIds.some((id) => (tagsMapRef.current.get(id) ?? id) === title);

    if (isTitleAlreadySelected) {
      setValue('');
      inputRef?.current?.focus();
      return;
    }

    const existingServerTag = (result.data.tags || []).find((t) => t.title === title);

    if (existingServerTag) {
      tagsMapRef.current.set(existingServerTag.id, existingServerTag.title);
      if (!tagIds.includes(existingServerTag.id)) {
        setTagIds([...tagIds, existingServerTag.id]);
        setIsChanged(true);
      }
      await resetTags();
      inputRef?.current?.focus();
      return;
    }

    tagsMapRef.current.set(title, title);

    if (!localTagTitles.includes(title)) {
      setLocalTagTitles([...localTagTitles, title]);
    }

    setTagIds([...tagIds, title]);
    setIsChanged(true);

    await resetTags();
    inputRef?.current?.focus();
  };

  const onLoadMore = (inView: boolean) => {
    if (inView) {
      loadMore(value);
    }
  };

  const onOptionClick = (tagId: string) => {
    setValue('');
    setIsChanged(true);

    if (!tagsMapRef.current.has(tagId)) {
      const serverTag = (result.data.tags || []).find((t) => t.id === tagId);
      if (serverTag) {
        tagsMapRef.current.set(tagId, serverTag.title);
      }
    }

    const currentTagIdIndex = tagIds.indexOf(tagId);
    if (currentTagIdIndex !== -1) {
      const newTagIds = [...tagIds];
      newTagIds.splice(currentTagIdIndex, 1);
      setTagIds(newTagIds);
    } else {
      setTagIds([...tagIds, tagId]);
    }
  };

  const optionAddTag = useMemo(
    () => ({
      key: 'linked_tags_modal_add_tag',
      isMulti: false,
      onClick: onAddTagClick,
      content: <span className={styles.CompanyTagsAutocompleteAddTag}>{TEXTS.MODAL_AUTOCOMPLETE_ADD_TAG_OPTION}</span>,
    }),
    [tagIds, value]
  );

  const optionLoadMoreTags = useMemo(
    () => ({
      key: 'linked_tags_modal_load_more_tags',
      isMulti: false,
      onClick: () => {},
      content: (
        <div className={styles.CompanyTagsAutocompleteLoadMoreTags}>
          <InView
            onChange={onLoadMore}
            key="linked_tags_modal_load_more_in_view">
            <Spinner color={Colors.COLOR_ACCENT_500} />
          </InView>
        </div>
      ),
    }),
    []
  );

  const options: DropdownItemDataType[] = useMemo(() => {
    const serverTagTitles = new Set((result.data.tags || []).map((t) => t.title));

    const localTags = localTagTitles
      .filter((title) => !serverTagTitles.has(title))
      .map((title) => ({
        key: title,
        selected: tagIds.includes(title),
        isMulti: true,
        onClick: () => onOptionClick(title),
        content: SuggestionUtils.createSuggestionPreview(title, value, 'mark'),
        value: title,
      }));

    const currentTags = (result.data.tags || []).map((item: CompanyTagType) => ({
      key: item.title,
      selected: tagIds.includes(item.id),
      isMulti: true,
      onClick: () => onOptionClick(item.id),
      content: SuggestionUtils.createSuggestionPreview(item.title, value, 'mark'),
      value: item.title,
    }));

    if (value.length === 0) {
      const res: DropdownItemDataType[] = [...localTags, ...currentTags];

      if (result.data.paginationInfo?.hasNextPage) {
        res.push(optionLoadMoreTags);
      }

      return res;
    }

    return [optionAddTag, ...localTags, ...currentTags];
  }, [result.data.tags, tagIds, localTagTitles]);

  const getFilterOptions = (filterFn: (tag: CompanyTagType) => boolean = () => true) => {
    const opts: DropdownItemDataType[] = (result.data.tags || []).reduce<DropdownItemDataType[]>((options, item) => {
      if (filterFn(item)) {
        options.push({
          key: item.id,
          selected: filterSelectedTags.some((tag) => tag.key === item.id),
          isMulti: true,
          onClick: () => {},
          content: SuggestionUtils.createSuggestionPreview(item.title, value, 'mark'),
          value: item.title,
        });
      }
      return options;
    }, []);

    if (result.data.paginationInfo?.hasNextPage) {
      opts.push(optionLoadMoreTags);
    }

    return opts;
  };

  const autocompletePlaceholder = useMemo(
    () => (isDesktop ? TEXTS.MODAL_AUTOCOMPLETE_PLACEHOLDER : TEXTS.MODAL_AUTOCOMPLETE_PLACEHOLDER_SHORT),
    [isDesktop]
  );

  const onChange = (value: string) => {
    const capped = value.slice(0, MAX_TAG_TITLE_LENGTH);
    setValue(capped.length === 1 ? capped.toUpperCase() : capped);
    debouncedRequest(capped);
  };

  const onRemoveTag = (currentTagId: string) => {
    const currentTagIdIndex = tagIds.indexOf(currentTagId);
    if (currentTagIdIndex !== -1) {
      const newTagIds = [...tagIds];
      newTagIds.splice(currentTagIdIndex, 1);
      setTagIds(newTagIds);
      setIsChanged(true);
    }
  };

  const onBackspacePressed = () => {
    if (value.length === 0) {
      const newTags = [...tagIds];
      const removed = newTags.pop();

      setTagIds(newTags);
      setIsChanged(true);

      if (removed && localTagTitles.includes(removed)) {
        setLocalTagTitles(localTagTitles.filter((t) => t !== removed));
      }
    }
  };

  const onEnterPressed = () => {
    if (value.length !== 0) {
      onAddTagClick();
    }
  };

  const onSetTagsSubmit = async (onSubmit: (tagNames: string[]) => Promise<void>) => {
    setIsSetLoading(true);
    const serverTagsById = new Map((result.data.tags || []).map((t) => [t.id, t.title]));
    const tagNames = tagIds.map((id) => tagsMapRef.current.get(id) ?? serverTagsById.get(id) ?? id);
    await onSubmit(tagNames);
    setLocalTagTitles([]);
    await resetTags();
    setIsSetLoading(false);
  };

  const extensions = useMemo(
    () =>
      tagIds.map((tagId: string) => {
        const tagTitle = tagsMapRef.current.get(tagId) || tagId;
        return (
          <CompanyTagChip
            key={tagId}
            id={tagTitle}
            title={tagTitle}
            context="modal"
            visibleTitleLength={isDesktop ? VISIBLE_TITLE_LENGTH : VISIBLE_TITLE_LENGTH_MOBILE}
            isDisabled={isSetLoading}
            onRemoveClick={() => onRemoveTag(tagId)}
          />
        );
      }),
    [tagIds, isSetLoading]
  );

  return (
    <CompanyTagsContext.Provider
      value={{
        value,
        options,
        getFilterOptions,
        filterSelectedTags,
        setFilterSelectedTags,
        maxVisibleOptions: AUTOCOMPLETE_MAX_VISIBLE_OPTIONS,
        menuMargin: !isDesktop ? AUTOCOMPLETE_DROPDOWN_MENU_MARGIN : undefined,
        extensions,
        inputRef,
        modalContentRef,
        isLoading: result.loading,
        isAddTagLoading: false,
        isSetLoading,
        isDesktop,
        isTagsSubmitDisabled: !isChanged,
        autocompletePlaceholder,
        initTags,
        getSelectedTagsSnapshot,
        onChange,
        onRemoveTag,
        onBackspacePressed,
        onEnterPressed,
        onSetTagsSubmit,
        tagsRequest: debouncedRequest,
      }}>
      {children}
    </CompanyTagsContext.Provider>
  );
};

export default CompanyTagsProvider;
